import React, { useState } from "react";
import { Info } from "lucide-react";
import MultilingualInput from "../../../../../components/admin/MultilingualInput/MultilingualInput";
import { useLanguage } from "../../../../../context/LanguageContext";
import { useAdminProductsTranslation } from "../../translations";

const BasicInfoTab = ({
    product,
    setProduct,
    handleChange,
    errors,
    categories,
    getTotalVariantQuantity,
    shouldValidateQuantities,
    getQuantityValidation,
    autoDistributeQuantity,
    activeTab,
    additionalImageFile,
    setAdditionalImageFile
}) => {
    const { language } = useLanguage();
    const { t } = useAdminProductsTranslation(language);
    const [tagInput, setTagInput] = useState("");

    // Handle tag input
    const handleTagInputChange = (e) => {
        setTagInput(e.target.value);
    };

    // Add tag
    const handleAddTag = (e) => {
        e.preventDefault();
        if (tagInput.trim() === "") return;

        if (!product.tags.includes(tagInput.trim())) {
            setProduct({
                ...product,
                tags: [...product.tags, tagInput.trim()]
            });
        }

        setTagInput("");
    };

    // Remove tag
    const handleRemoveTag = (tag) => {
        setProduct({
            ...product,
            tags: product.tags.filter(t => t !== tag)
        });
    };

    // Handle Enter key for adding tags
    const handleTagKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAddTag(e);
        }
    };

    // Handle additional image file selection
    const handleAdditionalImageSelect = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            const maxSize = 10 * 1024 * 1024; // 10MB

            if (!validTypes.includes(file.type)) {
                alert('Please select a valid image file (JPEG, PNG, or WebP)');
                event.target.value = '';
                return;
            }

            if (file.size > maxSize) {
                alert('Image size must be less than 10MB');
                event.target.value = '';
                return;
            }

            setAdditionalImageFile(file);
        }
    };

    // Remove additional image file
    const removeAdditionalImageFile = () => {
        setAdditionalImageFile(null);
        // Also clear the product imageUrl if it exists
        if (product.imageUrl) {
            setProduct({ ...product, imageUrl: '' });
        }
    };

    // Field info component
    const FieldInfo = ({ children }) => (
        <div className="field-info">
            <Info size={14} />
            <span>{children}</span>
        </div>
    );

    return (
        <div className={`editor-tab-content ${activeTab === "basic" ? "active" : ""}`}>
            <div className="editor-form-section">
                <h2>Product Information</h2>

                <div className="editor-form-row">
                    <div className="editor-form-group">
                        <MultilingualInput
                            label={t('productName')}
                            value={product.name}
                            onChange={(value) => handleChange('name', value)}
                            required={true}
                            error={errors.name}
                            placeholder={t('productNamePlaceholder')}
                        />
                        <FieldInfo>The name customers will see. Make it descriptive and easy to understand.</FieldInfo>
                    </div>
                </div>

                <div className="editor-form-row">
                    <div className="editor-form-group">
                        <MultilingualInput
                            label={t('description')}
                            value={product.description}
                            onChange={(value) => handleChange('description', value)}
                            isTextarea={true}
                            rows={5}
                            placeholder={t('descriptionPlaceholder')}
                        />

                    </div>
                </div>

                <div className="editor-form-row">
                    <div className="editor-form-group">
                        <MultilingualInput
                            label="Important Note"
                            value={product.note || ''}
                            onChange={(value) => handleChange('note', value)}
                            isTextarea={true}
                            rows={3}
                            placeholder="Optional: Add an important note for this product (e.g., pre-order 3 hours in advance, special instructions, etc.)"
                        />

                    </div>
                </div>
            </div>

            <div className="editor-form-section">
                <h2>Additional Information</h2>

                <div className="editor-form-row">
                    <div className="editor-form-group">
                        <label htmlFor="imageTitle">Image Title</label>
                        <FieldInfo>
                            Title for additional image (e.g., "Size Chart", "Care Instructions", "Nutrition Facts").
                        </FieldInfo>
                        <input
                            type="text"
                            id="imageTitle"
                            name="imageTitle"
                            value={product.imageTitle || ''}
                            onChange={handleChange}
                            placeholder="e.g., Size Chart"
                        />
                    </div>
                </div>

                <div className="editor-form-row">
                    <div className="editor-form-group">
                        <label htmlFor="additionalImage">Upload Additional Image</label>
                        <FieldInfo>
                            Upload an image to display when customers click the image title button (e.g., size chart, care instructions).
                        </FieldInfo>

                        <input
                            type="file"
                            id="additionalImage"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            onChange={handleAdditionalImageSelect}
                            style={{ marginBottom: '10px' }}
                        />

                        {(additionalImageFile || product.imageUrl) && (
                            <div className="image-preview" style={{ marginTop: '10px', position: 'relative' }}>
                                <img
                                    src={additionalImageFile ? URL.createObjectURL(additionalImageFile) : product.imageUrl}
                                    alt="Additional image preview"
                                    style={{
                                        maxWidth: '200px',
                                        maxHeight: '200px',
                                        objectFit: 'contain',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        display: 'block'
                                    }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={removeAdditionalImageFile}
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        background: '#ff4444',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '24px',
                                        height: '24px',
                                        fontSize: '14px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    title="Remove image"
                                >
                                    ×
                                </button>
                            </div>
                        )}

                        <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                            Supported formats: JPEG, PNG, WebP. Max size: 10MB
                        </div>
                    </div>
                </div>
            </div>

            <div className="editor-form-section">
                <h2>{t('price')}</h2>

                <div className="editor-form-row multiple-columns">
                    <div className="editor-form-group">
                        <label htmlFor="price">{t('price')} (AED) *</label>

                        <input
                            type="text"
                            id="price"
                            name="price"
                            value={product.price}
                            onChange={handleChange}
                            className={errors.price ? "has-error" : ""}
                            placeholder="299.00"
                        />
                        {errors.price && <div className="field-error">{errors.price}</div>}
                    </div>

                    {/* <div className="editor-form-group">
                        <label htmlFor="comparePrice">{t('comparePrice')}</label>
                        <FieldInfo>
                            Original price (if on sale). Shows customers the discount: "Was AED 399, Now AED 299"
                        </FieldInfo>
                        <input
                            type="text"
                            id="comparePrice"
                            name="comparePrice"
                            value={product.comparePrice}
                            onChange={handleChange}
                            placeholder="399.00"
                        />
                    </div> */}

                    <div className="editor-form-group">
                        <label htmlFor="cost">{t('cost')}</label>
                        <FieldInfo>
                            Your wholesale/production cost. Used for profit calculations and internal reporting only.
                        </FieldInfo>
                        <input
                            type="text"
                            id="cost"
                            name="cost"
                            value={product.cost}
                            onChange={handleChange}
                            placeholder="150.00"
                        />
                    </div>
                </div>
            </div>

            <div className="editor-form-section">
                <h2>Inventory</h2>

                <div className="editor-form-row multiple-columns">
                    <div className="editor-form-group">
                        <label htmlFor="sku">{t('sku')} (Stock Keeping Unit)</label>
                        <FieldInfo>
                            Unique identifier for inventory tracking. Use letters, numbers, or dashes (e.g., PHONE-SAM-S24-256).
                        </FieldInfo>
                        <input
                            type="text"
                            id="sku"
                            name="sku"
                            value={product.sku}
                            onChange={handleChange}
                            placeholder="PHONE-SAM-S24-256"
                        />
                    </div>

                    {/* <div className="editor-form-group">
                        <label htmlFor="barcode">{t('barcode')} (ISBN, UPC, GTIN, etc.)</label>
                        <FieldInfo>
                            Product barcode for scanning at checkout and inventory management. Enter the full barcode number.
                        </FieldInfo>
                        <input
                            type="text"
                            id="barcode"
                            name="barcode"
                            value={product.barcode}
                            onChange={handleChange}
                            placeholder="1234567890123"
                        />
                    </div> */}
                </div>

                <div className="editor-form-row multiple-columns">
                    <div className="editor-form-group">
                        <label htmlFor="quantity">
                            {t('quantity')}
                            {shouldValidateQuantities() && (
                                <span className="quantity-info">
                                    (Variants: {getTotalVariantQuantity()})
                                </span>
                            )}
                        </label>
                        <FieldInfo>
                            Total number of items in stock. If using variants, this should equal the sum of all variant quantities.
                        </FieldInfo>

                        {/* Unlimited quantity checkbox */}
                        <div className="unlimited-quantity-toggle" style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={product.quantity === -1 || product.quantity === '-1'}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            handleChange('quantity', -1);
                                        } else {
                                            handleChange('quantity', 0);
                                        }
                                    }}
                                    style={{ width: '18px', height: '18px' }}
                                />
                                <span>{t('unlimitedQuantity') || 'Unlimited Quantity'}</span>
                            </label>
                        </div>

                        {product.quantity !== -1 && product.quantity !== '-1' && (
                            <>
                                <input
                                    type="number"
                                    id="quantity"
                                    name="quantity"
                                    value={product.quantity}
                                    onChange={handleChange}
                                    min="0"
                                    className={errors.quantity ? "has-error" : ""}
                                    placeholder="100"
                                />
                                {errors.quantity && <div className="field-error">{errors.quantity}</div>}
                                {shouldValidateQuantities() && !getQuantityValidation().isValid && (
                                    <div className="quantity-actions">
                                        <button
                                            type="button"
                                            className="auto-distribute-btn"
                                            onClick={autoDistributeQuantity}
                                        >
                                            Auto-distribute remaining quantity
                                        </button>
                                    </div>
                                )}
                            </>
                        )}

                        {(product.quantity === -1 || product.quantity === '-1') && (
                            <div className="unlimited-indicator" style={{
                                padding: '10px',
                                background: '#e8f5e9',
                                borderRadius: '4px',
                                color: '#2e7d32',
                                fontSize: '14px'
                            }}>
                                ∞ {t('infiniteStock') || 'This product has infinite stock'}
                            </div>
                        )}
                    </div>

                    <div className="editor-form-group">
                        <label htmlFor="category">{t('category')} *</label>

                        <select
                            id="category"
                            name="category"
                            value={product.category}
                            onChange={handleChange}
                            className={errors.category ? "has-error" : ""}
                        >
                            <option value="">{t('selectCategory')}</option>
                            {categories.map(category => (
                                <option key={category.id} value={category.name}>
                                    {category.name}
                                </option>
                            ))}
                        </select>
                        {errors.category && <div className="field-error">{errors.category}</div>}
                    </div>

                    <div className="editor-form-group">
                        <label htmlFor="status">{t('status')}</label>

                        <select
                            id="status"
                            name="status"
                            value={product.status}
                            onChange={handleChange}
                        >
                            <option value="active">{t('active')} - Visible to customers</option>
                            <option value="draft">{t('draft')} - Hidden from customers</option>
                            <option value="out-of-stock">{t('outOfStock')} - Visible but not purchasable</option>
                            <option value="inactive">{t('inactive')} - Hidden from customers</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="editor-form-section">
                <h2>{t('tags')}</h2>
                <FieldInfo>
                    Tags help customers find your product through search. Use keywords that describe your product,
                    brand, features, or use cases. Examples: "smartphone", "samsung", "android", "camera", "gaming".
                </FieldInfo>

                <div className="editor-tags-container">
                    {product.tags.map((tag, index) => (
                        <div key={index} className="editor-tag">
                            <span>{tag}</span>
                            <button
                                type="button"
                                className="editor-tag-remove"
                                onClick={() => handleRemoveTag(tag)}
                                title={`Remove tag: ${tag}`}
                            >
                                ×
                            </button>
                        </div>
                    ))}
                    {product.tags.length === 0 && (
                        <div className="no-tags-placeholder">
                            No tags added yet. Add tags to help customers find your product.
                        </div>
                    )}
                </div>

                <div className="editor-form-row">
                    <div className="editor-tag-input-group">
                        <input
                            type="text"
                            value={tagInput}
                            onChange={handleTagInputChange}
                            onKeyPress={handleTagKeyPress}
                            placeholder={t('enterTag')}
                        />
                        <button
                            type="button"
                            className="editor-tag-add"
                            onClick={handleAddTag}
                            disabled={!tagInput.trim()}
                        >
                            {t('addTag')}
                        </button>
                    </div>
                </div>

                <div className="tags-help">
                    <strong>Tag Tips:</strong>
                    <ul>
                        <li>Use relevant keywords customers might search for</li>
                        <li>Include brand names, product features, and categories</li>
                        <li>Add color, size, or material descriptions</li>
                        <li>Think about how customers describe this product</li>
                        <li>Use both specific and general terms</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default BasicInfoTab;